import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CONDITIONS_THRESHOLDS, ConditionsScore } from "../utils/conditions";
import { ConditionsBadge } from "./area/conditionsScore";
import "./ratingsExplainer.css";

/// "How climb ratings work": one dialog for the whole app, opened from any rating's details

interface RatingsExplainerContextValue {
    open: () => void;
}

const RatingsExplainerContext = createContext<RatingsExplainerContextValue>({ open: () => {} });

export function useRatingsExplainer() {
    return useContext(RatingsExplainerContext);
}

/// Opens "How climb ratings work" from a key line or legend
export function RatingsHelpLink({ children = "How ratings work" }: { children?: ReactNode }) {
    const { open } = useRatingsExplainer();
    return (
        <button type="button" className="ratings-help-link" onClick={open}>{children}</button>
    );
}

const LEVELS: [ConditionsScore, string][] = [
    [5, "Comfortable temperatures, dry and calm."],
    [4, "Good climbing, with one small issue like a breeze."],
    [3, "Climbable, but heat, cold, wind or showers will bother you."],
    [2, "Several problems. Check the hourly before you go."],
    [1, "Rain, wet rock, or extreme heat or cold."],
];

const lowest = (steps: [number, number][]) => Math.min(...steps.map(([min]) => min));

export function RatingsExplainerProvider({ children }: { children: ReactNode }) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) {
            return;
        }
        if (isOpen && !dialog.open) {
            // showModal traps focus and handles Escape; older browsers (and jsdom) only get `open`
            if (typeof dialog.showModal === "function") {
                dialog.showModal();
            } else {
                dialog.setAttribute("open", "");
            }
        } else if (!isOpen && dialog.open) {
            if (typeof dialog.close === "function") {
                dialog.close();
            } else {
                dialog.removeAttribute("open");
            }
        }
    }, [isOpen]);

    const t = CONDITIONS_THRESHOLDS;

    return (
        <RatingsExplainerContext.Provider value={{ open }}>
            {children}
            <dialog
                ref={dialogRef}
                className="ratings-explainer"
                aria-labelledby="ratings-explainer-title"
                onClose={close}
                onClick={(e) => {
                    // A click on the backdrop lands on the dialog element itself
                    if (e.target === e.currentTarget) {
                        close();
                    }
                }}
            >
                <div className="ratings-explainer__body">
                    <div className="ratings-explainer__header">
                        <h2 className="ratings-explainer__title" id="ratings-explainer-title">How climb ratings work</h2>
                        <button type="button" className="ratings-explainer__close" onClick={close} aria-label="Close">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                    </div>
                    <p className="ratings-explainer__intro">
                        Every hour and day gets a climb rating from Prime down to Skip, based on the forecast
                        temperature, rain, wind and mugginess. It’s a guide, not a guarantee: check the hourly before you go.
                    </p>
                    <ul className="ratings-explainer__levels">
                        {LEVELS.map(([score, text]) => (
                            <li key={score}>
                                <ConditionsBadge score={score} />
                                <span>{text}</span>
                            </li>
                        ))}
                    </ul>
                    <h3 className="ratings-explainer__subtitle">What lowers a rating</h3>
                    <p className="ratings-explainer__note">A rating’s details only flag the things that actually lowered it.</p>
                    <ul className="ratings-explainer__factors">
                        <li><strong>Temperature</strong> outside {t.idealTempLow}–{t.idealTempHigh}°F (feels-like when the forecast has it, the daytime high for a whole day).</li>
                        <li><strong>Rain</strong> during the day: a {Math.round(lowest(t.precipProbability) * 100)}% chance or more, or measurable rain. {t.precipAccumulationWet}" or more means wet rock, which is always a Skip. Rain overnight counts toward the next day instead.</li>
                        <li><strong>Rain the night before</strong>: {lowest(t.overnightRain)}" or more overnight can leave rock damp the next morning, so it lowers that day’s rating and its hours until about {5 + t.dampMorningHours} AM. {t.overnightRain[0][0]}" or more lowers them further.</li>
                        <li><strong>Wind</strong>: {lowest(t.windSpeed)} mph or more, or gusts of {lowest(t.windGust)} mph or more.</li>
                        <li><strong>Muggy air</strong>: a dew point of {lowest(t.dewPoint)}°F or more. Dew point measures how much moisture is in the air, so humid but cool days don’t count.</li>
                    </ul>
                </div>
            </dialog>
        </RatingsExplainerContext.Provider>
    );
}
